import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { AttributeTypeFacetModel, AttributeValueFacetModel, FacetModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import { VARIANT_ATTRIBUTE_FACETS } from "@thkStore/Constants/ProductPropertiesKeys";
import { getVariantChildrenGridDataView } from "@thkStore/Store/Selectors/VariantChildrenGridSelectors";
import {
    CustomAttributeTypeFacetModel,
    SortType,
    VariantAttributeFacetModel,
    VariantChildrenGridApiParameter,
} from "@thkStore/Types/VariantChildrenGridModel";
import { mergeCustomProperties, CustomPropertiesByParentModel } from "@thkStore/Types/CustomPropertiesByParentModel";

const SORT_MAP: Record<SortType, string> = {
    BestMatch: "",
    ProductAToZ: "ASC",
    ProductZToA: "DESC",
};

interface UseServerSideVariantChildrenProps {
    productId: string;
    defaultPageSize: number;
    pageSizeOptionsStr: string;
    customAttributeTypeProperties: CustomPropertiesByParentModel[] | undefined;
    loadVariantChildrenPage: (parameter: VariantChildrenGridApiParameter) => void;
}

export const useServerSideVariantChildren = ({
    productId,
    defaultPageSize,
    pageSizeOptionsStr,
    customAttributeTypeProperties,
    loadVariantChildrenPage,
}: UseServerSideVariantChildrenProps) => {

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(defaultPageSize || 10);
    const [sortType, setSortType] = useState<SortType>("BestMatch");
    const [attributeValueIds, setAttributeValueIds] = useState<string[]>([]);
    const [searchWithinQueries, setSearchWithinQueries] = useState<string[]>([]);
    const [filterAttributeValueIds, setFilterAttributeValueIds] = useState<string[]>([]);
    const [searchText, setSearchText] = useState<string>("");

    const pageSizeOptions = useMemo(() => {
        return (pageSizeOptionsStr || "10,25,50,100")
            .split(",")
            .map(s => parseInt(s.trim(), 10))
            .filter(n => !Number.isNaN(n));
    }, [pageSizeOptionsStr]);

    const apiParameter = useMemo<VariantChildrenGridApiParameter>(() => {
        const param: VariantChildrenGridApiParameter = {
            productId,            
            variantChildrenGrid: true,
            page,
            pageSize,
        };
                
        const sort = SORT_MAP[sortType];
        if (sort) {
            param.sort = sort;
        }

        if (attributeValueIds.length > 0) {
            param.attributeValueIds = attributeValueIds;
        }

        if (searchWithinQueries.length > 0) {
            param.searchWithin = searchWithinQueries.join(" ");
        }

        if (filterAttributeValueIds.length > 0) {
            param.filterAttributeValueIds = filterAttributeValueIds;
        }

        if (searchText.trim()) {
            param.searchText = searchText.trim();
        }

        return param;
    }, [productId, page, pageSize, sortType, attributeValueIds, searchWithinQueries, filterAttributeValueIds, searchText]);

    useEffect(() => {
        loadVariantChildrenPage(apiParameter);
    }, [apiParameter]);

    const dataView = useSelector((state: ApplicationState) =>
        getVariantChildrenGridDataView(state, apiParameter),
    );      

    const variants: ProductModel[] | undefined = dataView.value as ProductModel[] | undefined;
    const isLoading = dataView.isLoading;
    const pagination = (dataView as any).pagination;

    const variantAttributeFacets = useMemo<VariantAttributeFacetModel[]>(() => {
        const properties = (dataView as any)?.properties;
        if (!properties || !properties[VARIANT_ATTRIBUTE_FACETS]) {
            return [];
        }
        try {
            return JSON.parse(properties[VARIANT_ATTRIBUTE_FACETS]) as VariantAttributeFacetModel[];
        } catch {
            return [];
        }
    }, [(dataView as any)?.properties]);

    const attributeFacets = useMemo<AttributeTypeFacetModel[]>(() => {
        return variantAttributeFacets.map(attr => ({
            attributeTypeId: attr.attributeTypeId,
            name: attr.name,
            nameDisplay: attr.name,
            sortOrder: attr.sortOrder,
            attributeValueFacets: attr.attributeValueFacets.map(av => ({
                attributeValueId: av.attributeValueId,
                value: av.value,
                valueDisplay: av.value,
                sortOrder: 0,
                count: av.count,
                selected: filterAttributeValueIds.includes(av.attributeValueId),
            })) as AttributeValueFacetModel[],
        })) as AttributeTypeFacetModel[];
    }, [variantAttributeFacets, filterAttributeValueIds]);  

    const attributeFacetsWithCustomProperties = useMemo<CustomAttributeTypeFacetModel[]>(() => {
        return mergeCustomProperties(
            attributeFacets,
            customAttributeTypeProperties ?? [],
            facet => facet.attributeTypeId,
        );
    }, [attributeFacets, customAttributeTypeProperties]);

    // --- Handler functions ---

    const handleSortChange = useCallback((newSortType: SortType) => {
        setSortType(newSortType);
        setPage(1);
    }, []);

    const handlePageChange = useCallback((newPage: number) => {
        setPage(newPage);
    }, []);

    const handlePageSizeChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = parseInt(event.currentTarget.value, 10);
        setPageSize(newPageSize);
        setPage(1);
    }, []);

    const handleFilterChange = useCallback((_attributeTypeId: string, facet: FacetModel) => {
        setFilterAttributeValueIds(prev => {
            if (facet.selected) {
                return [...prev, facet.id];
            }
            return prev.filter(id => id !== facet.id);
        });
        setPage(1);
    }, []);

    const handleRemoveFilter = useCallback((_attrTypeId: string, value: string) => {
        setFilterAttributeValueIds(prev => prev.filter(id => id !== value));
        setPage(1);
    }, []);

    const handleClearAllFilters = useCallback(() => {
        setAttributeValueIds([]);
        setSearchWithinQueries([]);
        setFilterAttributeValueIds([]);
        setSearchText("");
        setPage(1);
    }, []);

    const handleAddSearchFilters = useCallback((filterObj: { searchWithinQueries: string[] }) => {
        setSearchWithinQueries(prev => [...prev, ...filterObj.searchWithinQueries]);
        setPage(1);
    }, []);

    const handleRemoveSearchTerm = useCallback((searchTerm: string) => {
        setSearchWithinQueries(prev => prev.filter(q => q !== searchTerm));
        setPage(1);
    }, []);

    const handleTraitValueFilterChange = useCallback((traitValueId: string, selected: boolean) => {
        setFilterAttributeValueIds(prev => {
            if (selected) {
                return [...prev, traitValueId];
            }
            return prev.filter(id => id !== traitValueId);
        });
        setPage(1);
    }, []);

    const handleRemoveTraitValueFilter = useCallback((traitValueId: string) => {
        setFilterAttributeValueIds(prev => prev.filter(id => id !== traitValueId));
        setPage(1);
    }, []);

    const handleSearchTextChange = useCallback((text: string) => {
        setSearchText(text);
        setPage(1);
    }, []);

    return {
        variants,
        isLoading,
        pagination,
        attributeFacets,
        attributeFacetsWithCustomProperties,
        searchWithinQueries,
        sortType,
        currentPage: page,
        pageSize,
        pageSizeOptions,
        totalResults: pagination?.totalItemCount ?? 0,
        handleSortChange,
        handlePageChange,
        handlePageSizeChange,
        handleFilterChange,
        handleRemoveFilter,
        handleClearAllFilters,
        handleAddSearchFilters,
        handleRemoveSearchTerm,
        filterAttributeValueIds,
        searchText,
        handleTraitValueFilterChange,
        handleRemoveTraitValueFilter,
        handleSearchTextChange,
    };
};
